import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { EMAR_REASON_MIN, type AdministrationOutcome, type DoseState } from '@wodi/domain';
import type {
  AdministrationView,
  DoseView,
  MedicationChart,
  MedicationOrderView,
} from '@wodi/shared';
import {
  AlertPanel,
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  Kbd,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  SegmentedControl,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
  useShortcut,
  type TagState,
} from '@wodi/ui';
import { useRef, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { administrationInError, medicationChartQuery, recordAdministration } from '../api/emar.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { REASON_MIN_LENGTH } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { errorText } from './bed-board-page.tsx';
import styles from './nursing.module.css';
import { when } from './vitals-chart.tsx';

// The medicines chart of a stay (docs/plans/phase2-emar-inpatient-pharmacy.md §5): each order
// with its due doses, recording a dose as given, held, refused or missed, and the history of
// every dose recorded. Allergies are in the banner and checked again on each order. There is
// no drug-interaction check in Wodi, and nothing here says there is.

const route = getRouteApi('/_app/admissions/$admissionId/medicines');

export const DOSE_STATE_WORDS: Record<DoseState, { text: string; tag: TagState }> = {
  upcoming: { text: 'Upcoming', tag: 'neutral' },
  due: { text: 'Due', tag: 'pending' },
  overdue: { text: 'Overdue', tag: 'attention' },
  given: { text: 'Given', tag: 'done' },
  held: { text: 'Held', tag: 'neutral' },
  refused: { text: 'Refused', tag: 'neutral' },
  missed: { text: 'Missed', tag: 'attention' },
};

export const OUTCOME_WORDS: Record<AdministrationOutcome, string> = {
  given: 'Given',
  held: 'Held',
  refused: 'Refused',
  missed: 'Missed',
};

const TIMING_WORDS: Record<AdministrationView['timing'], string> = {
  on_time: 'On time',
  early: 'Early',
  late: 'Late',
  not_assessed: 'Timing not judged',
  unscheduled: 'As needed',
};

const ORDER_STATUS_WORDS: Record<MedicationOrderView['status'], string> = {
  active: 'Active',
  stopped: 'Stopped',
  cancelled: 'Cancelled',
  entered_in_error: 'Entered in error',
};

const ALERT_WORDS: Record<string, string> = {
  'emar.order_not_verified': 'The pharmacy has not verified this order yet.',
  'emar.prn_max_reached': 'The prescriber’s maximum doses for 24 hours have already been given.',
  'rx.allergy.match': 'This medicine matches a recorded allergy.',
  'rx.allergy.uncoded':
    'Some recorded allergies are not coded, so Wodi could not check them against this medicine.',
  'rx.allergy.not_matchable':
    'This medicine has no ingredients in the formulary, so Wodi could not check it against the allergies.',
  'rx.allergy.not_recorded': 'Allergies are not recorded for this patient, so none were checked.',
};

const OUTCOMES = [
  { id: 'given', label: 'Given' },
  { id: 'held', label: 'Held' },
  { id: 'refused', label: 'Refused' },
  { id: 'missed', label: 'Missed' },
] as const;

function placeWords(chart: MedicationChart): string {
  const a = chart.admission;
  if (a.ward === null) return 'Medicine orders and doses in this stay.';
  return `${a.ward.name}${a.bedLabel === null ? ', waiting for a bed' : `, bed ${a.bedLabel}`}.`;
}

function windowWords(chart: MedicationChart): string {
  const { earlyWindowMinutes: early, lateWindowMinutes: late } = chart.windows;
  if (early === null && late === null)
    return 'Dose timing windows are not set up, so no dose is marked early, late or overdue.';
  const parts = [
    early === null ? null : `early more than ${String(early)} minutes before`,
    late === null ? null : `late more than ${String(late)} minutes after`,
  ].filter((p) => p !== null);
  return `A dose is ${parts.join(', and ')} its time.`;
}

function verificationWords(order: MedicationOrderView): string {
  const v = order.verification;
  if (v === null) return 'Not verified by pharmacy';
  return `Verified by ${v.verifiedBy.displayName}: ${v.supply === 'ward_stock' ? 'ward stock' : 'patient supply'}`;
}

/** The alerts a given dose must answer, as the chart knows them before saving. */
function knownAlerts(order: MedicationOrderView): string[] {
  const out = order.allergyFindings.filter((f) => f.tier === 'interruptive').map((f) => f.ruleId);
  if (order.verification === null) out.push('emar.order_not_verified');
  if (
    order.frequencyKind === 'as_needed' &&
    order.maxDosesPerDay !== null &&
    order.givenInLastDay >= order.maxDosesPerDay
  )
    out.push('emar.prn_max_reached');
  return out;
}

interface Recording {
  order: MedicationOrderView;
  dose: DoseView | null;
}

function RecordDoseDialog({
  chart,
  recording,
  onClose,
}: {
  chart: MedicationChart;
  recording: Recording;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const { order, dose } = recording;
  const blocked = order.allergyFindings.some((f) => f.tier === 'blocking');
  const [outcome, setOutcome] = useState<AdministrationOutcome>(blocked ? 'held' : 'given');
  const [reason, setReason] = useState('');
  const [ackReason, setAckReason] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [extraAlerts, setExtraAlerts] = useState<string[]>([]);
  const requestId = useRef(crypto.randomUUID());
  const wardStock = order.verification?.supply === 'ward_stock';
  const alerts = outcome === 'given' ? [...new Set([...knownAlerts(order), ...extraAlerts])] : [];
  const reasonNeeded = outcome !== 'given';
  const quantityValue = Number.parseInt(quantity, 10);
  const quantityOk =
    !wardStock || outcome !== 'given' || (quantityValue > 0 && quantityValue < 1001);
  const ready =
    online &&
    !(blocked && outcome === 'given') &&
    (!reasonNeeded || reason.trim().length >= EMAR_REASON_MIN) &&
    (alerts.length === 0 || ackReason.trim().length >= REASON_MIN_LENGTH) &&
    quantityOk;
  const save = useMutation({
    mutationFn: () =>
      recordAdministration(chart.admission.id, {
        clientRequestId: requestId.current,
        prescriptionLineId: order.lineId,
        scheduledFor: dose?.scheduledFor ?? null,
        outcome,
        ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
        ...(wardStock && outcome === 'given' ? { wardStockQuantity: quantityValue } : {}),
        acknowledgements: alerts.map((ruleId) => ({ ruleId, reason: ackReason.trim() })),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(['inpatient', 'medicines', chart.admission.id], next);
      toasts.show({ title: 'Dose recorded' });
      onClose();
    },
    onError: (error) => {
      requestId.current = crypto.randomUUID();
      // An alert the chart did not know of yet (the record changed): show it to answer.
      if (error instanceof ApiError && error.code === 'alert_acknowledgement_required') {
        const ids = error.context?.['ruleIds'];
        if (Array.isArray(ids)) setExtraAlerts(ids.map(String));
      }
    },
  });
  useShortcut(
    'mod+enter',
    () => {
      if (ready && !save.isPending) save.mutate();
    },
    { allowInFields: true, description: 'Save the dose' },
  );
  return (
    <Dialog
      title="Record dose"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <form
        className={styles.form}
        aria-label="Record dose"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) save.mutate();
        }}
      >
        <p className={styles.meta}>
          {`${order.drugDisplay}. ${order.doseDisplay}.${dose === null ? ' As needed.' : ` Due ${when(dose.scheduledFor)}.`} Recorded as now, by you.`}
        </p>
        {blocked ? (
          <Notice tone="error">
            A recorded allergy blocks this medicine. Do not give it: record the dose as held and
            tell the prescriber.
          </Notice>
        ) : null}
        <SegmentedControl
          label="Outcome"
          options={OUTCOMES}
          value={outcome}
          onChange={(id) => {
            setOutcome(id as AdministrationOutcome);
          }}
        />
        {reasonNeeded ? (
          <TextArea
            label="Why was it not given?"
            value={reason}
            onChange={setReason}
            rows={2}
            description="Required when a dose is not given."
          />
        ) : null}
        {wardStock && outcome === 'given' ? (
          <TextField
            label={`Taken from ward stock (${order.stockUnit ?? 'stock units'})`}
            value={quantity}
            onChange={setQuantity}
            inputMode="numeric"
            width="xs"
            {...(quantityOk ? {} : { errorMessage: 'Enter whole units, 1 or more.' })}
          />
        ) : null}
        {alerts.length > 0 ? (
          <AlertPanel
            danger={alerts.some((id) => id === 'rx.allergy.match')}
            title="Check before giving this dose."
            reason={ackReason}
            onReasonChange={setAckReason}
            reasonLabel="Why you are giving it"
          >
            <ul>
              {alerts.map((id) => (
                <li key={id}>
                  {ALERT_WORDS[id] ?? 'An alert on this medicine needs your answer.'}
                </li>
              ))}
            </ul>
          </AlertPanel>
        ) : null}
        {save.error === null ? null : (
          <Notice tone="error">
            {errorText(save.error, 'The dose was not saved. Try again.')}
          </Notice>
        )}
        <DialogActions>
          <Button variant="quiet" onPress={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!ready}
            isPending={save.isPending}
            pendingLabel="Saving…"
          >
            Save dose
          </Button>
          <Kbd combo="mod+enter" />
        </DialogActions>
      </form>
    </Dialog>
  );
}

function InErrorDialog({
  admissionId,
  administration,
  onClose,
}: {
  admissionId: string;
  administration: AdministrationView;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const mark = useMutation({
    mutationFn: () => administrationInError(administration.id, reason.trim()),
    onSuccess: (next) => {
      queryClient.setQueryData(['inpatient', 'medicines', admissionId], next);
      toasts.show({ title: 'Dose entered in error' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Enter dose in error"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>
        The record stays in the history, marked entered in error, with your reason. A scheduled dose
        becomes due again.
      </p>
      <TextArea label="Why is it in error?" value={reason} onChange={setReason} rows={2} />
      {mark.error === null ? null : (
        <Notice tone="error">
          {errorText(mark.error, 'The record was not changed. Try again.')}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Keep record
        </Button>
        <Button
          variant="primary"
          isDisabled={reason.trim().length < REASON_MIN_LENGTH}
          isPending={mark.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            mark.mutate();
          }}
        >
          Enter in error
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function OrderItem({
  order,
  mayRecord,
  onRecord,
}: {
  order: MedicationOrderView;
  mayRecord: boolean;
  onRecord: (recording: Recording) => void;
}) {
  const online = useOnline();
  const active = order.status === 'active';
  const blocking = order.allergyFindings.some((f) => f.tier === 'blocking');
  return (
    <li className={styles.item} aria-label={order.drugDisplay}>
      <div className={styles.itemHead}>
        <strong>{order.drugDisplay}</strong>
        <span className={styles.actions}>
          {blocking ? <Tag state="danger">Allergy conflict</Tag> : null}
          {order.isControlled ? <Tag state="attention">Controlled</Tag> : null}
          <Tag state={active ? 'active' : 'neutral'}>{ORDER_STATUS_WORDS[order.status]}</Tag>
        </span>
      </div>
      <p className={styles.text}>{order.doseDisplay}</p>
      {order.instructions === null ? null : <p className={styles.meta}>{order.instructions}</p>}
      <p className={styles.meta}>
        {`${verificationWords(order)} · Prescribed by ${order.prescriberDisplay}, ${when(order.signedAt)} · `}
        <span className="wodi-id">{order.prescriptionNumber}</span>
      </p>
      {order.statusReason === null ? null : (
        <p
          className={styles.meta}
        >{`${ORDER_STATUS_WORDS[order.status]}: ${order.statusReason}`}</p>
      )}
      {order.frequencyKind === 'as_needed' ? (
        <div className={styles.actions}>
          <span className={styles.meta}>
            {`As needed. Given ${String(order.givenInLastDay)} in the last 24 hours${order.maxDosesPerDay === null ? '' : ` (most ${String(order.maxDosesPerDay)})`}.`}
          </span>
          {mayRecord && active ? (
            <Button
              dense
              isDisabled={!online}
              onPress={() => {
                onRecord({ order, dose: null });
              }}
            >
              Record dose
            </Button>
          ) : null}
        </div>
      ) : order.doses.length === 0 ? (
        <p className={styles.meta}>No doses in the chart window.</p>
      ) : (
        <ul className={styles.list} aria-label={`Doses of ${order.drugDisplay}`}>
          {order.doses.map((dose) => {
            const words = DOSE_STATE_WORDS[dose.state];
            const open = dose.administrationId === null;
            return (
              <li key={dose.scheduledFor} className={styles.actions}>
                <span className={styles.numeric}>{when(dose.scheduledFor)}</span>
                <Tag state={words.tag}>{words.text}</Tag>
                {mayRecord && active && open ? (
                  <Button
                    dense
                    variant={dose.state === 'upcoming' ? 'quiet' : 'secondary'}
                    isDisabled={!online}
                    onPress={() => {
                      onRecord({ order, dose });
                    }}
                  >
                    Record dose
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function History({
  chart,
  mayRecord,
  userId,
}: {
  chart: MedicationChart;
  mayRecord: boolean;
  userId: string | null;
}) {
  const online = useOnline();
  const [marking, setMarking] = useState<AdministrationView | null>(null);
  return (
    <Section title="Doses recorded" description="Every dose in this stay, newest first.">
      <Table flat dense aria-label="Doses recorded in this stay">
        <TableHeader>
          <Column isRowHeader>When</Column>
          <Column>Medicine</Column>
          <Column>Outcome</Column>
          <Column>Timing</Column>
          <Column>By</Column>
          <Column>State</Column>
        </TableHeader>
        <TableBody
          items={chart.administrations}
          renderEmptyState={() => 'No doses recorded in this stay yet.'}
        >
          {(a) => (
            <Row id={a.id}>
              <Cell>
                <span className={styles.numeric}>{when(a.administeredAt)}</span>
              </Cell>
              <Cell>{a.drugDisplay}</Cell>
              <Cell>
                {`${OUTCOME_WORDS[a.outcome]}${a.reason === null ? '' : `: ${a.reason}`}${a.wardStockQuantity === null ? '' : ` (${String(a.wardStockQuantity)} from ward stock)`}`}
              </Cell>
              <Cell>{TIMING_WORDS[a.timing]}</Cell>
              <Cell>{a.administeredBy.displayName}</Cell>
              <Cell>
                {a.status === 'entered_in_error' ? (
                  <span>
                    <Tag state="neutral">Entered in error</Tag>
                    {a.statusReason === null ? null : (
                      <span className={styles.meta}>{` ${a.statusReason}`}</span>
                    )}
                  </span>
                ) : mayRecord && a.administeredBy.id === userId ? (
                  <Button
                    variant="quiet"
                    dense
                    isDisabled={!online}
                    onPress={() => {
                      setMarking(a);
                    }}
                  >
                    Enter in error
                  </Button>
                ) : (
                  <Tag state="done">Recorded</Tag>
                )}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      {marking === null ? null : (
        <InErrorDialog
          admissionId={chart.admission.id}
          administration={marking}
          onClose={() => {
            setMarking(null);
          }}
        />
      )}
    </Section>
  );
}

export function MedicinesPage() {
  const { admissionId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const chart = useQuery(medicationChartQuery(admissionId));
  const [recording, setRecording] = useState<Recording | null>(null);

  if (session === null || !can(session, 'clinical.read') || !can(session, 'inpatient.read')) {
    return (
      <Notice tone="info">
        You don't have permission to see inpatient medicine records. Ask an administrator if you
        need it.
      </Notice>
    );
  }
  if (chart.isPending) return <LoadingBlock label="Loading medicines" rows={8} />;
  if (chart.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void chart.refetch()}>Try again</Button>}>
        {errorText(chart.error, 'Medicines could not be loaded. Try again.')}
      </Notice>
    );
  }
  const view = chart.data;
  const admitted = view.admission.status === 'admitted';
  const mayRecord = admitted && can(session, 'medication.administer');
  const active = view.orders.filter((o) => o.status === 'active');
  const ended = view.orders.filter((o) => o.status !== 'active');
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={view.admission.patientId} />
      <PageHeader
        title="Medicines"
        description={placeWords(view)}
        actions={
          <>
            <Link to="/admissions/$admissionId/nursing" params={{ admissionId }}>
              Nursing
            </Link>
            <Link to="/admissions/$admissionId" params={{ admissionId }}>
              Back to the stay
            </Link>
          </>
        }
      />
      {!online ? (
        <Notice tone="info">No connection to Wodi. Recording doses waits for it.</Notice>
      ) : null}
      {!admitted ? (
        <Notice tone="info">
          This stay has ended. Its medicine records are shown; no dose can be recorded.
        </Notice>
      ) : null}
      <Section
        title="Medicine orders"
        description={`${windowWords(view)} Orders come from ward rounds; the prescriber stops or changes them.`}
      >
        {active.length === 0 ? (
          <p className={styles.meta}>No active medicine orders in this stay.</p>
        ) : (
          <ul className={styles.list} aria-label="Active medicine orders">
            {active.map((order) => (
              <OrderItem
                key={order.lineId}
                order={order}
                mayRecord={mayRecord}
                onRecord={setRecording}
              />
            ))}
          </ul>
        )}
        {ended.length === 0 ? null : (
          <ul className={styles.list} aria-label="Stopped and cancelled orders">
            {ended.map((order) => (
              <OrderItem
                key={order.lineId}
                order={order}
                mayRecord={false}
                onRecord={setRecording}
              />
            ))}
          </ul>
        )}
      </Section>
      <History chart={view} mayRecord={mayRecord} userId={session.user.id} />
      {recording === null ? null : (
        <RecordDoseDialog
          chart={view}
          recording={recording}
          onClose={() => {
            setRecording(null);
          }}
        />
      )}
    </div>
  );
}
